import { AnimatePresence, motion } from "framer-motion";
import {
    Home,
    User,
    Settings,
    FileText,
    Bell,
    MessageSquare,
    ChartAreaIcon,
    Shield,
    DollarSign,
    X,
    LogOut
} from "lucide-react";
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { BiAnalyse } from "react-icons/bi";
import { LogoutManually, LogoutUser, useAppDispatch } from "../../../../redux";

const SidebarComponent = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const sidebarItems = [
        { icon: <Home className="h-5 w-5" />, label: "Dashboard", href: "/clients" },
        { icon: <User className="h-5 w-5" />, label: "Profile", href: "/client/profile", active: true },
        { icon: <Settings className="h-5 w-5" />, label: "Settings", href: "/client/profilesettings" },
        { icon: <FileText className="h-5 w-5" />, label: "My Orders", href: "/client/myorders" },
        { icon: <Bell className="h-5 w-5" />, label: "My Offers", href: "/client/myoffers" },
        { icon: <MessageSquare className="h-5 w-5" />, label: "Messages", href: "/client/messageandvideocall" },
        { icon: <ChartAreaIcon className="h-5 w-5" />, label: "Progress", href: "/client/progress" },
        { icon: <Shield className="h-5 w-5" />, label: "Support Tickets", href: "/client/support" },
        { icon: <DollarSign className="h-5 w-5" />, label: "Wallet", href: "/client/Wallet" },
        { icon: <BiAnalyse className="h-5 w-5" />, label: "Report", href: "/client/reports" },
        {
            icon: <LogOut className="h-5 w-5" />,
            label: "Log Out",
            href: "/logout"
        },
    ];

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        } finally {
            navigate("/");
        }
    };

    return (
        <>
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r shadow-sm overflow-y-auto">
                <nav className="p-4 space-y-1">
                    {sidebarItems.map((item, index) => (
                        <NavLink
                            key={index}
                            to={item.href}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${isActive || item.active
                                    ? "text-blue-600 bg-blue-50 font-medium"
                                    : "text-gray-600 hover:bg-gray-50"
                                }`
                            }>
                            {item.icon}
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
            </aside>

            {/* Mobile Sidebar */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                        />
                        <motion.aside
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                            className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl z-50 lg:hidden flex flex-col"
                        >
                            <div className="flex items-center justify-between p-4 border-b">
                                <div className="flex items-center gap-3">
                                    <img
                                        src="https://assests.netlify.app/assets/images/logo.png"
                                        alt="Logo"
                                        className="h-8 w-8"
                                    />
                                    <span className="text-blue-600 text-lg font-bold">Kokobeo</span>
                                </div>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Scrollable menu items */}
                            <div className="flex-1 overflow-y-auto">
                                <nav className="p-4">
                                    {sidebarItems.map((item, index) => (
                                        item.href === "/logout" ? (
                                            <button
                                                key={index}
                                                onClick={handleLogout}
                                                className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                                {item.icon}
                                                <span>{item.label}</span>
                                            </button>
                                        ) :
                                            <NavLink
                                                key={index}
                                                to={item.href}
                                                className={({ isActive }) =>
                                                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors mb-1 ${isActive || item.active
                                                        ? "text-blue-600 bg-blue-50 font-medium"
                                                        : "text-gray-600 hover:bg-gray-50"
                                                    }`
                                                }
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                                {item.icon}
                                                <span>{item.label}</span>
                                            </NavLink>
                                    ))}
                                </nav>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default SidebarComponent;
